import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { siteQueries } from '../api/resources/site';
import type { FeatureFlags, SiteSettings } from '../api/types';
import { cacheSiteIcon, siteIconSrc } from '../components/layout/Logo';
import { i18next } from '../i18n';

const OPTIMISTIC_FLAGS: FeatureFlags = {
  webRegistration: true,
  gameRegistration: true,
  avatarUpload: true,
  community: true,
  speedrunSubmission: true,
};

interface SiteContextValue {
  settings: SiteSettings | null;
  features: FeatureFlags;
  isLoading: boolean;
  isMaintenance: boolean;
}

const SiteContext = createContext<SiteContextValue | null>(null);

export function SiteProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery(siteQueries.config());
  const settings = settingsQuery.data ?? null;

  useEffect(() => {
    const onLanguageChanged = () => {
      void queryClient.invalidateQueries({ queryKey: ['site'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    };
    i18next.on('languageChanged', onLanguageChanged);
    return () => {
      i18next.off('languageChanged', onLanguageChanged);
    };
  }, [queryClient]);

  useEffect(() => {
    if (!settings) return;
    document.title = settings.siteName;
    document.querySelector('meta[name="description"]')?.setAttribute('content', settings.siteDescription);
    const mark = siteIconSrc(settings);
    const tab = settings.faviconUrl || settings.logoUrl || mark;
    document.querySelector<HTMLLinkElement>('link[rel="icon"]')?.setAttribute('href', tab);
    const bootLogo = document.querySelector<HTMLImageElement>('#boot-logo');
    if (bootLogo) bootLogo.src = mark;
    cacheSiteIcon(mark);
  }, [settings]);

  const value = useMemo<SiteContextValue>(
    () => ({
      settings,
      features: settings?.features ?? OPTIMISTIC_FLAGS,
      isLoading: settingsQuery.isLoading,
      isMaintenance: settings?.maintenance.enabled ?? false,
    }),
    [settings, settingsQuery.isLoading],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const context = useContext(SiteContext);
  if (!context) throw new Error('useSite must be used inside SiteProvider');
  return context;
}
